import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Trash2, 
  Eye, 
  EyeOff, 
  CheckSquare, 
  Square,
  AlertTriangle,
  Search,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminServicesBulk = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/services/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Kunne ikke hente tjenester');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (serviceId) => {
    setSelectedIds(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredServices();
    if (selectedIds.length === filtered.length && filtered.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(s => s.id));
    }
  };

  const getFilteredServices = () => {
    return services.filter(service => {
      const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          service.price.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'published' && service.published) ||
                           (filterStatus === 'unpublished' && !service.published);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('Ingen tjenester valgt');
      return;
    }

    const confirmMessages = {
      publish: `Publiser ${selectedIds.length} tjeneste(r)?`,
      unpublish: `Avpubliser ${selectedIds.length} tjeneste(r)?`,
      delete: `⚠️ PERMANENT SLETTING av ${selectedIds.length} tjeneste(r)?\n\nDette kan IKKE angres!`
    };

    if (!window.confirm(confirmMessages[action])) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = {
        publish: '/services/bulk-publish',
        unpublish: '/services/bulk-unpublish',
        delete: '/services/bulk-delete'
      }[action];

      const response = await axios.post(
        `${API}${endpoint}`,
        { service_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setSelectedIds([]);
      await fetchServices();
    } catch (error) {
      console.error(`Error ${action}ing services:`, error);
      toast.error(`Kunne ikke ${action === 'delete' ? 'slette' : action === 'publish' ? 'publisere' : 'avpublisere'} tjenester`);
    }
  };

  const filteredServices = getFilteredServices();
  const allSelected = filteredServices.length > 0 && selectedIds.length === filteredServices.length;

  if (loading) {
    return (
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="py-16 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-6"></div>
          <p className="text-gray-300 text-lg">Laster tjenester...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800/50 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-2xl text-white">Bulk-administrasjon av tjenester</CardTitle>
          <CardDescription className="text-gray-300 text-base">
            Velg flere tjenester for å publisere, avpublisere eller slette dem samtidig
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters - Improved contrast and spacing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Søk etter tjeneste eller pris..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full h-12 pl-12 pr-4 rounded-md bg-gray-700/50 border border-gray-600 text-white focus:border-red-500 focus:ring-red-500 cursor-pointer"
              >
                <option value="all">Alle kategorier</option>
                <option value="tungbil">Tungbil</option>
                <option value="tilhenger">Tilhenger</option>
                <option value="personbil">Personbil</option>
              </select>
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-12 px-4 rounded-md bg-gray-700/50 border border-gray-600 text-white focus:border-red-500 focus:ring-red-500 cursor-pointer"
            >
              <option value="all">Alle statuser</option>
              <option value="published">Kun publiserte</option>
              <option value="unpublished">Kun avpubliserte</option>
            </select>
          </div>

          {/* Sticky Bulk Actions - Always visible */}
          {selectedIds.length > 0 && (
            <div className="sticky top-4 z-10 bg-gradient-to-r from-blue-900/95 to-blue-800/95 backdrop-blur-sm p-5 rounded-lg border-2 border-blue-500 shadow-2xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CheckSquare className="w-6 h-6 text-blue-300" />
                  <span className="text-white font-semibold text-lg">
                    {selectedIds.length} tjeneste{selectedIds.length !== 1 ? 'r' : ''} valgt
                  </span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    onClick={() => handleBulkAction('publish')}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold border-2 border-green-500 shadow-lg"
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    Publiser
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => handleBulkAction('unpublish')}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-semibold border-2 border-orange-500 shadow-lg"
                  >
                    <EyeOff className="w-5 h-5 mr-2" />
                    Avpubliser
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => handleBulkAction('delete')}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold border-2 border-red-500 shadow-lg"
                  >
                    <Trash2 className="w-5 h-5 mr-2" />
                    Slett permanent
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Select All - Better contrast */}
          <div className="flex items-center justify-between py-4 px-2 border-y-2 border-gray-700 bg-gray-750">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                className="w-6 h-6 border-2 border-gray-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              />
              <span className="text-white font-semibold text-lg">
                {allSelected ? 'Avmerk alle' : 'Merk alle'} ({filteredServices.length})
              </span>
            </div>
            <span className="text-gray-300 font-medium">
              Totalt {services.length} tjeneste(r) i systemet
            </span>
          </div>

          {/* Services List - Dramatically improved */}
          <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2">
            {filteredServices.length === 0 ? (
              <div className="text-center py-20 text-gray-300">
                <AlertTriangle className="w-20 h-20 mx-auto mb-6 text-gray-500" />
                <p className="text-xl font-semibold mb-2">Ingen tjenester funnet</p>
                <p className="text-gray-400">Prøv å endre søkekriteriene</p>
              </div>
            ) : (
              filteredServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => toggleSelection(service.id)}
                  className={`group flex items-center p-5 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    selectedIds.includes(service.id)
                      ? 'bg-blue-900/40 border-blue-500 shadow-lg shadow-blue-500/20 scale-[1.02]'
                      : 'bg-gray-800/80 border-gray-600 hover:border-gray-500 hover:bg-gray-700/80 hover:scale-[1.01]'
                  }`}
                >
                  <div className="flex items-center space-x-5 flex-1">
                    <Checkbox
                      checked={selectedIds.includes(service.id)}
                      onCheckedChange={() => toggleSelection(service.id)}
                      className="w-7 h-7 border-2 border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h4 className="text-white font-bold text-lg">{service.title}</h4>
                        {!service.published && (
                          <Badge className="bg-orange-600 text-white border-orange-500 font-semibold px-3 py-1">
                            Avpublisert
                          </Badge>
                        )}
                        <Badge className="bg-gray-700 text-gray-200 border-gray-600 font-medium px-3 py-1">
                          {service.category}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 text-base">
                        <span className="font-bold text-green-400 text-lg">{service.price}</span>
                        {service.description && (
                          <span className="text-gray-300 truncate">{service.description}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer Info */}
          <div className="pt-4 border-t-2 border-gray-700">
            <p className="text-gray-400 text-sm text-center">
              💡 <strong>Tips:</strong> Klikk på en tjeneste for å velge den, eller bruk &quot;Merk alle&quot; for å velge alle synlige tjenester
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminServicesBulk;
